import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { LoggingService } from '../services/logging/logging.service';

/**
 * HTTP Debug Interceptor for E2E Testing and Development
 *
 * Provides enhanced logging for HTTP requests/responses to dramatically improve
 * debugging time when E2E tests fail (target: reduce from 30+ min to < 2 min).
 *
 * Features:
 * - Logs full HTTP request details (method, URL, headers sanitized, body presence)
 * - Logs response status, statusText, and duration
 * - For 404 errors: suggests closest matching endpoint ("Did you mean...?")
 * - Includes E2E test context when running in Playwright
 * - Generates unique request IDs for correlation
 * - Only active in development and E2E test modes (disabled in production)
 *
 * Related: FIN-0042 (E2E test investigation that motivated this interceptor)
 */
export const httpDebugInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const logger = inject(LoggingService);

  // Only enable in development or E2E test mode
  if (!shouldEnableDebugLogging()) {
    return next(req);
  }

  const requestId = generateRequestId();
  const startTime = performance.now();

  // Log request start
  logger.info('HTTP-REQUEST', {
    requestId,
    method: req.method,
    url: req.url,
    headers: sanitizeHeaders(req.headers),
    body: req.body ? 'present' : 'none',
    testContext: getTestContext()
  });

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        const duration = performance.now() - startTime;

        // Get connection details from Performance API
        const connectionDetails = getConnectionDetails(req.url, startTime);

        // Success log with enhanced connection info
        logger.info('HTTP-RESPONSE', {
          requestId,
          method: req.method,
          url: req.url,
          status: event.status,
          statusText: event.statusText,
          duration: `${duration.toFixed(2)}ms`,
          protocol: connectionDetails.protocol,
          connectionReused: connectionDetails.connectionReused,
          connectionId: connectionDetails.connectionId,
          timingBreakdown: connectionDetails.timingBreakdown,
          testContext: getTestContext()
        });
      }
    }),
    catchError((error: HttpErrorResponse) => {
      const duration = performance.now() - startTime;

      // Error log with suggestion
      logger.error('HTTP-ERROR', {
        requestId,
        method: req.method,
        url: req.url,
        status: error.status,
        statusText: error.statusText,
        duration: `${duration.toFixed(2)}ms`,
        errorBody: error.error,
        suggestion: getSuggestion(error),
        testContext: getTestContext()
      });

      return throwError(() => error);
    })
  );
};

/**
 * Get HTTP/2 connection details from Performance API
 * This helps diagnose connection pooling and multiplexing issues
 */
function getConnectionDetails(url: string, requestStartTime: number): ConnectionDetails {
  try {
    // Wait a bit for performance entry to be available
    const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

    // Find the most recent entry matching our URL that started around our request time
    const entry = entries
      .filter((e: PerformanceResourceTiming) => {
        const urlMatch = e.name.includes(url) || url.includes(e.name.split('?')[0]);
        const timeMatch = Math.abs(e.fetchStart - requestStartTime) < 100; // Within 100ms
        return urlMatch && timeMatch;
      })
      .sort((a, b) => b.startTime - a.startTime)[0];

    if (!entry) {
      return {
        protocol: 'unknown',
        connectionReused: false,
        connectionId: 'not-found',
        timingBreakdown: null
      };
    }

    // Extract connection details
    const protocol = entry.nextHopProtocol || 'unknown';
    const connectionReused = entry.connectEnd === 0 && entry.connectStart === 0;

    // Generate a pseudo-connection ID based on timing characteristics
    // Same connection = same DNS + connect times (or both zero for reused)
    const connectionId = generateConnectionId(entry);

    // Timing breakdown to identify stalls
    const timingBreakdown = {
      dns: entry.domainLookupEnd - entry.domainLookupStart,
      tcp: entry.connectEnd - entry.connectStart,
      ssl: entry.secureConnectionStart > 0 ? entry.connectEnd - entry.secureConnectionStart : 0,
      waiting: entry.responseStart - entry.requestStart,
      download: entry.responseEnd - entry.responseStart,
      // CRITICAL: Time spent in browser queue before request starts
      queueing: entry.fetchStart - entry.startTime,
      // CRITICAL: Stalled time (waiting for connection to become available)
      stalled: entry.requestStart - entry.fetchStart
    };

    return {
      protocol,
      connectionReused,
      connectionId,
      timingBreakdown
    };
  } catch (error) {
    console.warn('[HTTP Debug] Failed to get connection details:', error);
    return {
      protocol: 'error',
      connectionReused: false,
      connectionId: 'error',
      timingBreakdown: null
    };
  }
}

/**
 * Generate a pseudo-connection ID based on timing characteristics
 * Same connection = same timing fingerprint
 */
function generateConnectionId(entry: PerformanceResourceTiming): string {
  if (entry.connectEnd === 0 && entry.connectStart === 0) {
    // Connection reused - use domain + protocol as ID
    return `reused-${entry.nextHopProtocol || 'unknown'}`;
  }

  // New connection - use timing fingerprint
  const fingerprint = `${entry.domainLookupStart.toFixed(0)}-${entry.connectEnd.toFixed(0)}`;
  return `conn-${fingerprint}`;
}

/**
 * Connection details interface
 */
interface ConnectionDetails {
  protocol: string;
  connectionReused: boolean;
  connectionId: string;
  timingBreakdown: {
    dns: number;
    tcp: number;
    ssl: number;
    waiting: number;
    download: number;
    queueing: number;
    stalled: number;
  } | null;
}

/**
 * Generate a unique request ID for correlation
 */
function generateRequestId(): string {
  return `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Check if debug logging should be enabled
 * Enabled in: development mode, E2E tests
 * Disabled in: production
 */
function shouldEnableDebugLogging(): boolean {
  // Always enable in E2E tests (Playwright detection)
  if (isPlaywrightTest()) {
    return true;
  }

  // Enable in development (localhost)
  if (typeof window !== 'undefined') {
    return window.location.hostname === 'localhost' ||
           window.location.hostname.startsWith('127.0.0.1') ||
           window.location.hostname.includes('dev.finderwise.com');
  }

  return false;
}

/**
 * Detect if running in Playwright E2E test
 */
function isPlaywrightTest(): boolean {
  if (typeof window === 'undefined') return false;

  const win = window as any;
  return win.__PLAYWRIGHT_TEST__ === true ||
         win.playwright !== undefined ||
         navigator.webdriver === true;
}

/**
 * Get test context if running in E2E test
 */
function getTestContext(): TestContext | null {
  if (!isPlaywrightTest()) {
    return null;
  }

  const win = window as any;
  return {
    testName: win.__TEST_NAME__ || 'unknown',
    testFile: win.__TEST_FILE__ || 'unknown'
  };
}

/**
 * Sanitize headers to remove sensitive information
 */
function sanitizeHeaders(headers: any): Record<string, string> {
  const sanitized: Record<string, string> = {};
  const sensitiveHeaders = ['authorization', 'cookie', 'x-csrf-token'];

  headers.keys().forEach((key: string) => {
    const lowerKey = key.toLowerCase();
    if (sensitiveHeaders.includes(lowerKey)) {
      sanitized[key] = '***REDACTED***';
    } else {
      sanitized[key] = headers.get(key);
    }
  });

  return sanitized;
}

/**
 * Get suggestion for HTTP errors (especially 404s)
 */
function getSuggestion(error: HttpErrorResponse): string | null {
  if (error.status === 404 && error.url) {
    try {
      const path = new URL(error.url).pathname;

      // Known endpoints for comparison
      const knownEndpoints = [
        '/api/auth/refresh',
        '/api/auth/logout',
        '/api/auth/login',
        '/api/chat',
        '/api/chat/message',
        '/api/ws',
        '/api/health',
        '/actuator/health'
      ];

      // Find closest match using simple string similarity
      const closest = findClosestMatch(path, knownEndpoints);
      if (closest && closest !== path) {
        return `Endpoint not found. Did you mean '${closest}'?`;
      }

      return `Endpoint not found. Available endpoints: ${knownEndpoints.join(', ')}`;
    } catch (e) {
      // URL parsing failed, return generic message
      return 'Endpoint not found. Check backend @RequestMapping paths.';
    }
  }

  if (error.status === 401) {
    return 'Unauthorized. Check authentication token or CSRF configuration.';
  }

  if (error.status === 403) {
    return 'Forbidden. Check user permissions or CSRF token.';
  }

  if (error.status >= 500) {
    return 'Server error. Check backend logs for stack trace.';
  }

  return null;
}

/**
 * Find closest matching string using Levenshtein distance
 * Simple implementation - not optimized but good enough for small lists
 */
function findClosestMatch(target: string, candidates: string[]): string | null {
  if (candidates.length === 0) return null;

  let minDistance = Infinity;
  let closest: string | null = null;

  for (const candidate of candidates) {
    const distance = levenshteinDistance(target, candidate);
    if (distance < minDistance) {
      minDistance = distance;
      closest = candidate;
    }
  }

  // Only return if reasonably similar (distance < half the target length)
  if (closest && minDistance < target.length / 2) {
    return closest;
  }

  return null;
}

/**
 * Calculate Levenshtein distance between two strings
 * (edit distance - minimum number of single-character edits needed)
 */
function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;

  // Create 2D array for dynamic programming
  const matrix: number[][] = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));

  // Initialize first row and column
  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;

  // Fill the matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[len1][len2];
}

/**
 * Test context interface
 */
interface TestContext {
  testName: string;
  testFile: string;
}
